import { useSearchParams } from "@remix-run/react";
import { useQuery } from "@tanstack/react-query";

import { HWA_LAUNCH_FLAG } from "~/features/resources/dimensions";
import { useFeatureFlag } from "~/hooks/use-account-feature-flags";
import { useUserDetails } from "~/hooks/queries/use-user-details";

import { EMPTY_HWA_ACCESS, type HwaAccess } from "../types";

/**
 * Cache-key PREFIX, not a key. Use it to invalidate every viewer's entitlement at once
 * (react-query matches prefixes); use `hwaAccessQueryKey` to read or write one viewer's.
 */
export const HWA_ACCESS_QUERY_KEY = ["hwa-access"] as const;

/**
 * AIDEV-NOTE: Profile-scoped on purpose. An in-place session change (account switch, a
 * re-login in the same tab) keeps the QueryClient alive, so an unscoped key would serve
 * the PREVIOUS viewer's unlocked courses to the new one. The id comes from the `["user"]`
 * query, which is removed on SIGNED_OUT and invalidated on SIGNED_IN — so the key changes
 * with the viewer and the old entry is never read again.
 */
export function hwaAccessQueryKey(profileId: string | undefined | null) {
    return [...HWA_ACCESS_QUERY_KEY, profileId ?? null] as const;
}

/**
 * What the viewer may open in the Healing Works Academy.
 *
 * AIDEV-NOTE: Fails CLOSED whenever there is NO answer yet — first load, a network error,
 * an unresolved viewer, or the `hwa-launch` flag being off all resolve to "nothing
 * unlocked". Surfaces must render `isLoading` as a SKELETON, never as the locked state:
 * an owner lands on Resources on every visit, and painting locks and prices before the
 * entitlement arrives told a paying customer they had not paid.
 *
 * A background refetch keeps serving the last answer rather than reverting to locked. That
 * is deliberate and is NOT the access decision: the gate that matters is server-side
 * (`canViewResourcePage`, which is what mints or withholds the signed Cloudflare URL). This
 * hook only decides chrome, so revalidating it must not flicker the whole page.
 */
export function useHwaAccess(): { access: HwaAccess; isLoading: boolean } {
    const hwaReleased = useFeatureFlag(HWA_LAUNCH_FLAG);
    // `?hwa=unlocked` is set by the checkout success_url and means "a grant is on its way".
    const [searchParams] = useSearchParams();
    const awaitingCheckoutGrant = searchParams.get("hwa") === "unlocked";
    // Same `["user"]` query the flag above reads — no extra request.
    const { data: userDetails } = useUserDetails();
    const profileId = userDetails?.profile?.id;

    const { data, isPending, isError } = useQuery<HwaAccess>({
        queryKey: hwaAccessQueryKey(profileId),
        enabled: hwaReleased && !!profileId,
        queryFn: async () => {
            const res = await fetch("/api/hwa/access");
            if (!res.ok) return EMPTY_HWA_ACCESS;
            return (await res.json()) as HwaAccess;
        },
        // Entitlement changes on return from checkout, so don't serve a stale "locked".
        staleTime: 0,
        // AIDEV-NOTE: Stripe's success redirect races its own webhook — the buyer can land
        // back on the course before `checkout.session.completed` has written the row, and a
        // single mount-time read would then show a paying customer a locked course until
        // they reloaded. While `?hwa=unlocked` is on the URL and nothing is unlocked yet,
        // poll briefly; stop as soon as access arrives so the steady state is still one
        // request per mount.
        refetchInterval: (query) => {
            if (!awaitingCheckoutGrant) return false;
            const current = query.state.data;
            if (current && current.unlockedCourses.length > 0) return false;
            return 2000;
        },
        // AIDEV-NOTE: The root QueryClient sets `refetchOnMount: false`, so staleTime: 0
        // alone only MARKS the entry stale — returning to Resources would re-render the
        // cached answer and never re-ask. A grant or a lapse would leave the paywall wrong
        // until a hard reload, so this query opts back in.
        refetchOnMount: true,
    });

    if (!hwaReleased) return { access: EMPTY_HWA_ACCESS, isLoading: false };

    // AIDEV-NOTE: `isFetching` is deliberately NOT part of this. It is only "unresolved"
    // when we have never had an answer; once we do, a refetch revalidates it in the
    // background and the viewer keeps seeing the last server truth.
    const isResolving = !profileId || (isPending && !data);
    if (isResolving || isError || !data) {
        return { access: EMPTY_HWA_ACCESS, isLoading: isResolving };
    }

    return { access: data, isLoading: false };
}
